import httpClient from '../../../api/httpClient';
import API_CONFIG from '../../../config/api.config';
import { 
  AppointmentOrderData,
  PaymentSplitData,
  CreateOrderData,
  CreatePaymentData,
  UpdateOrderData
} from '../types/appointmentOrders.types';

const ENDPOINT = API_CONFIG.ENDPOINTS.APPOINTMENT_ORDERS || '/appointment-orders';

// Types are now imported from appointmentOrders.types.ts

export const appointmentOrdersApi = {
  /**
   * Create a new appointment order
   */
  async createOrder(orderData: CreateOrderData): Promise<AppointmentOrderData> {
    try {
      const response = await httpClient.post(ENDPOINT, orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating appointment order:', error);
      throw error;
    }
  },

  /**
   * Create multiple appointment orders in a batch
   */
  async createOrders(orders: CreateOrderData[]): Promise<AppointmentOrderData[]> {
    try {
      const response = await httpClient.post(`${ENDPOINT}/batch`, { orders });
      return response.data;
    } catch (error) {
      console.error('Error creating appointment orders:', error);
      throw error;
    }
  },

  /**
   * Record a payment for an order
   */
  async createPayment(paymentData: CreatePaymentData): Promise<PaymentSplitData> {
    try {
      const response = await httpClient.post(`${ENDPOINT}/payments`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Error recording payment:', error);
      throw error;
    }
  },

  /**
   * Record multiple payments in a batch
   */
  async createPayments(payments: CreatePaymentData[]): Promise<PaymentSplitData[]> {
    try {
      const response = await httpClient.post(`${ENDPOINT}/payments/batch`, { payments });
      return response.data;
    } catch (error) {
      console.error('Error recording payments:', error);
      throw error;
    }
  },

  /**
   * Update a single appointment order by ID
   */
  async updateOrderById(id: string, order: Omit<UpdateOrderData, 'id'>): Promise<AppointmentOrderData> {
    try {
      console.log('[API] PATCH', `${ENDPOINT}/${id}`, order);
      const response = await httpClient.patch(`${ENDPOINT}/${id}`, order);
      return response.data;
    } catch (error) {
      console.error('Error updating appointment order:', error);
      throw error;
    }
  },

  /**
   * Batch update (patch) appointment orders
   */
  /**
   * Batch update (patch) appointment orders
   * Always sends body as { updates: orders } to match backend DTO
   */
  /**
   * Batch update (patch) appointment orders
   * Accepts a payload object { updates: UpdateOrderData[] } to match backend DTO
   */
  async updateOrders(payload: { updates: UpdateOrderData[] }): Promise<AppointmentOrderData[]> {
    try {
      // Send the payload as-is to match backend expectation
      const response = await httpClient.patch(`${ENDPOINT}/batch`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating appointment orders:', error);
      throw error;
    }
  },
  /**
   * Get orders by appointment ID
   */
  async getOrdersByAppointment(appointmentId: string): Promise<AppointmentOrderData[]> {
    try {
      const response = await httpClient.get(`${ENDPOINT}/appointment/${appointmentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment orders:', error);
      throw error;
    }
  },

  /**
   * Get payments by order ID
   */
  getPaymentsByOrder: async (orderId: string) => {
    const response = await httpClient.get(`/payment-receive-order/by-appointment-order/${orderId}`);
    return response.data;
  },

  /**
   * Get payment collections summary by appointment
   */
  getPaymentCollectionsSummary: async (appointmentId: string) => {
    const response = await httpClient.get(`/payment-receive-order/collections-summary/${appointmentId}`);
    return response.data;
  },

  /**
   * Get appointment orders by date range (inclusive)
   */
  async getOrdersByDateRange(from: string, to: string): Promise<AppointmentOrderData[]> {
    try {
      const response = await httpClient.get(`/api/appointment-orders/report?from=${from}&to=${to}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment orders by date range:', error);
      throw error;
    }
  }
}